<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'
import { useRouter, useRoute } from 'vue-router'

const props = defineProps<{ collapsed: boolean; activePopup: string | null }>()
const emit = defineEmits<{
  (e: 'toggle'): void
  (e: 'toggle-popup', popupId: string): void
  (e: 'close-popup'): void
}>()

const { t } = useI18n()
const deviceStore = useDeviceStore()
const router = useRouter()
const route = useRoute()
const eqPreview = import.meta.env.DEV
  && new URLSearchParams(window.location.search).has('eqPreview')

interface MenuItem {
  id: string
  icon: string
  iconHover: string
  label: string
  route?: string
  popup?: string
  show: boolean
}

const menuItems = computed<MenuItem[]>(() => [
  {
    id: 'eq',
    icon: '/img/nav/EQsettings.png',
    iconHover: '/img/nav/EQ_hover_checked.png',
    label: t('eq.title', 'EQ Settings'),
    route: '/eq',
    show: deviceStore.deviceCapabilities.hasEq || eqPreview,
  },
  {
    id: 'enc',
    icon: '/img/nav/AIENC.png',
    iconHover: '/img/nav/ENC_hover_checked.png',
    label: deviceStore.isHeadPhone ? 'AI ENC' : 'ENC',
    popup: 'noise',
    show: deviceStore.deviceCapabilities.hasNoiseReduction || eqPreview,
  },
  {
    id: 'voice',
    icon: '/img/nav/voice.png',
    iconHover: '/img/nav/voice_hover.png',
    label: t('nav.voice'),
    route: '/voice',
    show: deviceStore.deviceCapabilities.hasVoiceChange,
  },
  {
    id: 'chiji',
    icon: '/img/nav/positioning.png',
    iconHover: '/img/nav/positioning__hover_checked.png',
    label: t('main.chickenDinner', 'Positioning'),
    route: '/chiji',
    show: deviceStore.deviceCapabilities.hasSpecialAudio || eqPreview,
  },
  {
    id: 'ai',
    icon: '/img/nav/AIhelper.png',
    iconHover: '/img/nav/AIhelper_hover.png',
    label: t('nav.ai'),
    route: '/ai',
    show: false, // AI助手暂时隐藏，可根据需要启用
  },
])

// 计算当前活动菜单
const activeMenuId = computed(() => {
  if (props.activePopup === 'noise') return 'enc'
  const path = route.path
  const item = menuItems.value.find(m => m.route && path.startsWith(m.route))
  return item?.id || ''
})

// 导航到指定页面
const navigateTo = async (item: MenuItem) => {
  // 弹窗自身支持开关：如果当前已打开，再点一次直接关闭（对齐旧版）
  if (item.popup && props.activePopup === item.popup) {
    emit('close-popup')
    return
  }

  // 互斥：打开任意一个入口前，先关闭当前弹窗
  if (props.activePopup) {
    emit('close-popup')
  }

  // ENC/AI ENC 为弹窗：必须回到主页上弹出（对齐旧版：不和 EQ/吃鸡页面共存）
  if (item.popup) {
    if (route.path !== '/') {
      await router.push('/')
    }
    emit('toggle-popup', item.popup)
    return
  }

  if (!item.route) return
  if (activeMenuId.value === item.id) {
    await router.push('/')
    return
  }
  await router.push(item.route)
}

const toggleCollapse = () => {
  emit('toggle')
}
</script>

<template>
  <div class="sidebar" :class="{ collapsed: props.collapsed }">
    <!-- 标志 -->
    <div class="logo-div">
      <router-link to="/">
        <img src="/img/logo.png" alt="logo" />
      </router-link>
    </div>

    <!-- 导航图标 -->
    <div class="nav-icons">
      <div
        v-for="item in menuItems"
        :key="item.id"
        v-show="item.show"
        class="nav-item"
        :class="{ active: activeMenuId === item.id }"
        @click="navigateTo(item)"
      >
        <div class="nav-icon-wrapper">
          <img class="nav-icon nav-icon-default" :src="item.icon" :alt="item.label" />
          <img class="nav-icon nav-icon-hover" :src="item.iconHover" :alt="item.label" aria-hidden="true" />
        </div>
        <p class="nav-tooltip">{{ item.label }}</p>
      </div>
    </div>

    <button
      v-if="route.path !== '/eq' && !deviceStore.isMicroPhone"
      class="sidebar-collapse-btn"
      type="button"
      :aria-label="props.collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="toggleCollapse"
    >
      <template v-if="props.collapsed">
        <img class="collapse-icon collapse-icon-default" src="/img/nav/sidebar_on.png" alt="" />
      </template>
      <template v-else>
        <img class="collapse-icon collapse-icon-default" src="/img/nav/sidebar_off.png" alt="" />
      </template>
    </button>
  </div>
</template>

<style scoped lang="scss">
.sidebar {
  width: var(--sidebar-width);
  height: 100vh;
  background: rgba(124, 124, 124, 0.2);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 101;
  overflow: visible;
}

.logo-div {
  width: 34px;
  height: 34px;
  margin: 20px auto 13px;

  img {
    width: 34px;
    height: 34px;
    object-fit: contain;
  }
}

.nav-icons {
  display: flex;
  flex-direction: column;
  margin-top: 28px;
  width: 100%;
  align-items: center;
}

.nav-item {
  position: relative;
  width: 100%;
  height: 58px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  margin: 8px auto;
  cursor: pointer;
  background: transparent;

  // 悬浮只切换视觉素材，不改变真正的 active/route 状态。
  &:hover {
    .nav-icon-default {
      opacity: 0;
    }

    .nav-icon-hover {
      opacity: 1;
    }

    .nav-tooltip {
      display: inline-flex;
    }
  }

  // 当前选中项始终保持与 hover 一致的蓝色 checked 图。
  &.active {
    .nav-icon-default {
      opacity: 0;
    }

    .nav-icon-hover {
      opacity: 1;
    }
  }
}

.nav-icon-wrapper {
  position: relative;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.nav-icon {
  position: absolute;
  inset: 0;
  width: 42px;
  height: 42px;
  object-fit: contain;
  pointer-events: none;
  transition: opacity 0.12s ease;
}

.nav-icon-default {
  opacity: 1;
}

.nav-icon-hover {
  opacity: 0;
}

.nav-tooltip {
  display: none;
  position: absolute;
  left: 57px;
  top: 10px;
  padding: 8px;
  margin: 0;
  font-size: 12px;
  color: rgb(255, 255, 255);
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 8px;
  white-space: nowrap;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.sidebar-collapse-btn {
  position: absolute;
  top: 50%;
  right: 0;
  // 目标尺寸按侧边栏正确效果收回：按钮高度 72px，不再使用上一版过大的 92px。
  // sidebar_off.png 左侧本身约有 5px 透明留白，因此向左补偿 5px，
  // 让真正可见的按钮边框与侧边栏右边界无缝衔接。
  transform: translate(calc(100% - 5px), -50%);
  width: max-content;
  height: 64px;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  cursor: pointer;
  z-index: 5;

  .collapse-icon {
    display: block;
    width: auto;
    height: 64px;
    max-width: none;
    object-fit: contain;
    pointer-events: none;
  }
}

.sidebar.collapsed {
  background: transparent;
  border-right: none;
}

.sidebar.collapsed .logo-div,
.sidebar.collapsed .nav-icons {
  display: none;
}

.sidebar.collapsed .sidebar-collapse-btn {
  right: auto;
  left: 0;
  // 收起状态贴住屏幕左边缘；使用同样的 72px 高度，两个状态视觉尺度一致。
  transform: translateY(-50%);
}
</style>
